// Lógica pura de edición del diseño (sin React). Todo en SI.
import type { Attached, BodyTube, FinSet, InnerTube, NoseCone, Parachute, Placement, Rocket, StackComponent, Transition } from '@openrocket-web/physics';

export type StackKind = 'nose-cone' | 'body-tube' | 'transition';
export type InertKind = 'shock-cord' | 'mass-component' | 'centering-ring' | 'bulkhead' | 'engine-block' | 'launch-lug';
export type AttachedKind = 'fin-set' | 'inner-tube' | 'parachute' | InertKind;
export type ComponentKind = StackKind | AttachedKind;

export const KIND_LABEL: Readonly<Record<ComponentKind, string>> = {
  'nose-cone': 'Ojiva',
  'body-tube': 'Tubo de cuerpo',
  transition: 'Transición',
  'fin-set': 'Aletas',
  'inner-tube': 'Tubo interior',
  parachute: 'Paracaídas',
  'shock-cord': 'Cordón elástico',
  'mass-component': 'Masa',
  'centering-ring': 'Anillo de centrado',
  bulkhead: 'Disco de enganche',
  'engine-block': 'Retén de motor',
  'launch-lug': 'Soporte para guía',
};

const PREFIX: Readonly<Record<ComponentKind, string>> = {
  'nose-cone': 'nose',
  'body-tube': 'tube',
  transition: 'transition',
  'fin-set': 'fins',
  'inner-tube': 'inner',
  parachute: 'chute',
  'shock-cord': 'cord',
  'mass-component': 'mass',
  'centering-ring': 'ring',
  bulkhead: 'bulkhead',
  'engine-block': 'block',
  'launch-lug': 'lug',
};

export type AnyComponent = StackComponent | Attached;

/** Todos los componentes (pila y adjuntos) con su padre, en orden de aparición. */
export function allComponents(rocket: Rocket): Array<{ component: AnyComponent; parentId: string | undefined }> {
  const out: Array<{ component: AnyComponent; parentId: string | undefined }> = [];
  for (const c of rocket.stack) {
    out.push({ component: c, parentId: undefined });
    if (c.type !== 'nose-cone') for (const k of c.children) out.push({ component: k, parentId: c.id });
  }
  return out;
}

export const findComponent = (rocket: Rocket, id: string | undefined): AnyComponent | undefined =>
  id === undefined ? undefined : allComponents(rocket).find((e) => e.component.id === id)?.component;

export function nextId(rocket: Rocket, kind: ComponentKind): string {
  const used = new Set(allComponents(rocket).map((e) => e.component.id));
  for (let n = 1; ; n++) {
    const id = `${PREFIX[kind]}-${n}`;
    if (!used.has(id)) return id;
  }
}

/** Diámetro interior del tubo o transición que contiene al componente (m). */
const hostInnerDiameter = (h: BodyTube | Transition): number => (h.type === 'body-tube' ? h.outerDiameter : Math.min(h.foreDiameter, h.aftDiameter)) - 2 * h.wallThickness;

export function defaultComponent(rocket: Rocket, kind: ComponentKind, host?: BodyTube | Transition): AnyComponent {
  const id = nextId(rocket, kind);
  const name = KIND_LABEL[kind];
  const lastDiameter = [...rocket.stack].reverse().map((c) => (c.type === 'nose-cone' ? c.baseDiameter : c.type === 'body-tube' ? c.outerDiameter : c.aftDiameter))[0] ?? 0.041;
  switch (kind) {
    case 'nose-cone':
      return { id, name, type: 'nose-cone', shape: 'ogive', length: 0.12, baseDiameter: lastDiameter, wallThickness: 0.0015, filled: false, material: 'polystyrene' } satisfies NoseCone;
    case 'body-tube':
      return { id, name, type: 'body-tube', length: 0.3, outerDiameter: lastDiameter, wallThickness: 0.001, material: 'cardboard', children: [] } satisfies BodyTube;
    case 'transition':
      return { id, name, type: 'transition', length: 0.06, foreDiameter: lastDiameter, aftDiameter: lastDiameter * 0.6, wallThickness: 0.001, material: 'cardboard', children: [] } satisfies Transition;
    case 'fin-set':
      return { id, name, type: 'fin-set', finCount: 3, rootChord: 0.07, tipChord: 0.035, span: 0.05, sweep: 0.03, thickness: 0.003, crossSection: 'rounded', material: 'balsa', position: { anchor: 'aft', offset: 0 } } satisfies FinSet;
    case 'inner-tube':
      return { id, name, type: 'inner-tube', length: 0.07, innerDiameter: 0.0185, wallThickness: 0.0005, material: 'cardboard', position: { anchor: 'aft', offset: 0 } } satisfies InnerTube;
    case 'parachute':
      return { id, name, type: 'parachute', diameter: 0.3, dragCoefficient: 0.8, material: 'ripstop-nylon', packedLength: 0.08, deployment: { event: 'ejection' }, position: { anchor: 'front', offset: 0.02 } } satisfies Parachute;
    default:
      return defaultInert(id, name, kind, host);
  }
}

/** Componentes inertes: los valores por defecto salen del tubo que los contiene y quedan dentro de él. */
function defaultInert(id: string, name: string, kind: InertKind, host: BodyTube | Transition | undefined): Attached {
  const hostLength = host?.length ?? 0.3;
  const inner = host === undefined ? 0.039 : hostInnerDiameter(host);
  const mount = host?.children.find((k): k is InnerTube => k.type === 'inner-tube');
  const mountOuter = mount === undefined ? undefined : mount.innerDiameter + 2 * mount.wallThickness;
  const front = (offset: number, length: number): Placement => ({ anchor: 'front', offset: Math.max(0, Math.min(offset, hostLength - length)) });
  switch (kind) {
    case 'shock-cord':
      return { id, name, type: 'shock-cord', length: 0.05, cordLength: 0.4, material: 'elastic-cord', position: front(0.02, 0.05) };
    case 'mass-component':
      return { id, name, type: 'mass-component', length: 0.03, mass: 0.01, position: front(0.05, 0.03) };
    case 'centering-ring':
      return { id, name, type: 'centering-ring', length: 0.003, outerDiameter: inner, innerDiameter: mountOuter ?? inner / 2, material: 'cardboard', position: { anchor: 'aft', offset: 0 } };
    case 'bulkhead':
      return { id, name, type: 'bulkhead', length: 0.003, outerDiameter: inner, innerDiameter: 0, material: 'birch-plywood', position: front(0.05, 0.003) };
    case 'engine-block': {
      const outer = mount?.innerDiameter ?? Math.min(0.0185, inner);
      // En el extremo delantero del soporte de motor, si lo hay.
      const position: Placement = mount === undefined ? { anchor: 'aft', offset: 0 } : mount.position.anchor === 'front' ? front(mount.position.offset, 0.005) : { anchor: 'aft', offset: Math.max(0, mount.position.offset + mount.length - 0.005) };
      return { id, name, type: 'engine-block', length: 0.005, outerDiameter: outer, innerDiameter: Math.max(outer - 0.006, outer / 2), material: 'cardboard', position };
    }
    case 'launch-lug':
      return { id, name, type: 'launch-lug', length: 0.03, outerDiameter: 0.006, wallThickness: 0.0008, material: 'cardboard', position: front(0.1, 0.03) };
  }
}

export const isStackKind = (k: ComponentKind): k is StackKind => k === 'nose-cone' || k === 'body-tube' || k === 'transition';

/**
 * Añade un componente. Los de pila van al final (el cono, al inicio). Los adjuntos van al tubo o
 * transición `parentId` y, si no se indica, al primer tubo (o transición).
 */
export function addComponent(rocket: Rocket, kind: ComponentKind, parentId?: string): { rocket: Rocket; id: string } | undefined {
  if (isStackKind(kind)) {
    const component = defaultComponent(rocket, kind);
    const stack = kind === 'nose-cone' ? [component as StackComponent, ...rocket.stack] : [...rocket.stack, component as StackComponent];
    return { rocket: { ...rocket, stack }, id: component.id };
  }
  const host = (parentId === undefined ? undefined : rocket.stack.find((c) => c.id === parentId && c.type !== 'nose-cone')) ?? rocket.stack.find((c) => c.type === 'body-tube') ?? rocket.stack.find((c) => c.type === 'transition');
  if (host === undefined || host.type === 'nose-cone') return undefined;
  const component = defaultComponent(rocket, kind, host);
  const stack = rocket.stack.map((c) => (c.id === host.id && c.type !== 'nose-cone' ? ({ ...c, children: [...c.children, component as Attached] } as StackComponent) : c));
  return { rocket: { ...rocket, stack }, id: component.id };
}

/** Aplica un cambio de propiedades a un componente (sin validar: de eso se encarga la validación del diseño). */
export function updateComponent(rocket: Rocket, id: string, patch: Record<string, unknown>): Rocket {
  const stack = rocket.stack.map((c): StackComponent => {
    if (c.id === id) return { ...c, ...patch } as StackComponent;
    if (c.type === 'nose-cone') return c;
    if (!c.children.some((k) => k.id === id)) return c;
    return { ...c, children: c.children.map((k) => (k.id === id ? ({ ...k, ...patch } as Attached) : k)) } as StackComponent;
  });
  return { ...rocket, stack };
}

export function removeComponent(rocket: Rocket, id: string): Rocket {
  const stack = rocket.stack
    .filter((c) => c.id !== id)
    .map((c): StackComponent => (c.type === 'nose-cone' ? c : ({ ...c, children: c.children.filter((k) => k.id !== id) } as StackComponent)));
  return { ...rocket, stack };
}

/** Mueve un componente de la pila `delta` posiciones (−1 hacia la punta, +1 hacia la cola). */
export function moveComponent(rocket: Rocket, id: string, delta: -1 | 1): Rocket {
  const i = rocket.stack.findIndex((c) => c.id === id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= rocket.stack.length) return rocket;
  const stack = [...rocket.stack];
  [stack[i], stack[j]] = [stack[j]!, stack[i]!];
  return { ...rocket, stack };
}

export const emptyRocket = (): Rocket => ({ name: 'Cohete Sin Nombre', stack: [] });

/** Diseño de ejemplo: ojiva, tubo con paracaídas, soporte de motor y aletas. */
export function exampleRocket(motorId?: string): Rocket {
  return {
    name: 'Cohete de ejemplo',
    stack: [
      { id: 'nose-1', name: 'Ojiva', type: 'nose-cone', shape: 'ogive', length: 0.12, baseDiameter: 0.041, wallThickness: 0.0015, filled: false, material: 'polystyrene' },
      {
        id: 'tube-1', name: 'Tubo de cuerpo', type: 'body-tube', length: 0.35, outerDiameter: 0.041, wallThickness: 0.001, material: 'cardboard',
        children: [
          { id: 'chute-1', name: 'Paracaídas', type: 'parachute', diameter: 0.3, dragCoefficient: 0.8, material: 'ripstop-nylon', packedLength: 0.08, deployment: { event: 'ejection' }, position: { anchor: 'front', offset: 0.02 } },
          { id: 'mount-1', name: 'Soporte de motor', type: 'inner-tube', length: 0.07, innerDiameter: 0.0185, wallThickness: 0.0005, material: 'cardboard', position: { anchor: 'aft', offset: 0 }, motorMount: motorId === undefined ? {} : { motor: { motorId, ejectionDelay: 5 } } },
          { id: 'fins-1', name: 'Aletas', type: 'fin-set', finCount: 3, rootChord: 0.07, tipChord: 0.035, span: 0.05, sweep: 0.03, thickness: 0.003, crossSection: 'rounded', material: 'balsa', position: { anchor: 'aft', offset: 0 } },
        ],
      },
    ],
  };
}
